/*app\javascript\dashboard\api\contacts.js*/

/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload, cancelToken) {
    // Debug logging for tracking filter API calls (controlled by RESPONZA_DEBUG)
    if (window.chatwootConfig?.responzaDebug === 'true' || process.env?.RESPONZA_DEBUG === 'true') {
      console.group('[RESPONZA_DEBUG] ContactAPI.filter called');
      console.log('Timestamp:', new Date().toISOString());
      console.log('Page:', page);
      console.log('Sort Attribute:', sortAttr);
      console.log('Query Payload:', JSON.stringify(queryPayload));
      console.log('Has Cancel Token:', !!cancelToken);
      console.log('Call Stack:', new Error().stack);
      
      // Try to identify the calling component
      const stack = new Error().stack;
      if (stack.includes('ContactsIndex')) {
        console.warn('Called from ContactsIndex component');
      } else if (stack.includes('Contact.vue')) {
        console.warn('Called from Contact bubble component');
      } else if (stack.includes('store')) {
        console.warn('Called from Vuex store');
      }
      
      // Check for phone number patterns
      const payloadStr = JSON.stringify(queryPayload);
      if (payloadStr.match(/\d{10,15}/)) {
        console.error('ALERT: Phone number pattern detected in filter payload!');
        console.error('This might be the bulk message issue pattern');
      }
      
      console.groupEnd();
    }
    
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    const config = cancelToken ? { cancelToken } : {};
    return axios.post(requestURL, queryPayload, config);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  batchDestroy(contactIds) {
    return axios.delete(`${this.url}/batch_destroy`, {
      data: { contact_ids: contactIds }
    });
  }
}

export default new ContactAPI();